<template>
  <div class="auth-login-facebook" @click="triggerLoginDialog">
    <img v-lazy-load  data-src="~/assets/images/icons/facebook.webp" alt="Facebook">
    <span>Sign in with Facebook</span>
    <ElementsRecaptcha ref="recaptchaRef" />
  </div>
</template>

<script lang="ts" setup>
// EMIT Definitions
//----------------------------------------------------------------------------------------------------------------------
import {Ref} from 'vue';

const emit = defineEmits<{
  (event: 'update:loading', payload: boolean): void;
  (event: 'update:response', payload: 200 | 201 | 403 | 406 | 407 | 500 | null): void;
}>();

// PROPS Definition
//----------------------------------------------------------------------------------------------------------------------
const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  response: {
    type: [Number, null] as PropType<200 | 201 | 403 | 406 | 407 | 500 | null>,
    default: null,
  },
});

// DATA Definitions
//----------------------------------------------------------------------------------------------------------------------
const {$register, $login} = useNuxtApp();
const recaptchaRef: Ref<HTMLElement | null> = ref(null);

/**
 * @_MOUNTED - Hook
 */
onMounted(() => {
  (window as any).FB.getLoginStatus((response: any) => {
    statusChangeCallback(response);
  });
});

/**
 * Handle the response object from Facebook
 *
 * @param response
 */
async function statusChangeCallback (response: any) {
  try {
    if (response && response.authResponse) {
      emit('update:loading', true);
      const url = `https://graph.facebook.com/v15.0/me?fields=first_name,last_name,email,picture&access_token=${response.authResponse.accessToken}`;
      const avatarUrlResponse = await fetch(url);
      if (!avatarUrlResponse.ok) {
        throw new Error(`HTTP error! status: ${avatarUrlResponse.status}`);
      }
      const data = await avatarUrlResponse.json();
      if (data.first_name && data.last_name && data.email && data.id) {
        // generate recaptcha token
        const token = await (recaptchaRef.value as any).validate();
        // Request to register
        const registrationResponse = await $register({
          firstName: data.first_name,
          lastName: data.last_name,
          email: data.email,
          avatarUrl: data.picture.data.url,
          token,
          socialLoginData: {
            provider: 'facebook',
            token: data.id,
          },
        });

        // Registration failed
        if (registrationResponse.code !== 201) {
          emit('update:response', registrationResponse.code);
        }

        // Registration successful and proceed to login
        if (registrationResponse.code === 201) {
          const loginResponse = await $login({
            email: data.email,
            socialLoginData: {
              provider: 'facebook',
              token: data.id,
            },
          });

          if (loginResponse.code !== 200) {
            emit('update:response', loginResponse.code);
          }
        }
      } else {
        emit('update:response', 500);
      }
    }
  } catch (error) {
    console.error('Error:', error);
    emit('update:response', 500);
  }
}

/**
 * Trigger the Facebook login dialog
 */
function triggerLoginDialog () {
  (window as any).FB.login((response: any) => {
    if (response.status === 'connected') {
      statusChangeCallback(response);
    } else {
      // The person is not logged into your webpage or we are unable to tell.
    }
  }, {scope: 'public_profile,email'});
}
</script>

<style lang="scss">
.auth-login-facebook {
  width: 200px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 12px;
  margin: 12px 0;
  border-radius: 4px;
  color: #fff;
  cursor: pointer;
  box-shadow: 0 0 4px 1px rgba(0, 0, 0, 0.25);
  background: #3b5998;
  transition: background 0.2s ease-in-out;

  &:hover {
    background: #2a4374;
    transition: background 0.2s ease-in-out;
  }

  img {
    max-width: 22px;
    max-height: 22px;
  }

  span {
    margin-left: 12px;
    font-size: 13px;
  }

  &.btn-register-with-google {
    //
  }
}
</style>
